import React, { useState, useEffect } from "react";
import {
  useGetOnboardingReportsQuery,
  useCountOnboardingReportsQuery,
  useUpdateOnboardingReportStatusMutation,
} from "../services/onboarding-report.service";
import Pagination from "../../../components/pagination";
import usePagination from "../../../customHooks/usePagination";
import { OnboardingReport, OnboardingReportAttachment } from "../types/onboarding-report.entity";
import { FaFileAlt, FaMoon, FaSun, FaTimes, FaExpand, FaCheckCircle, FaExternalLinkAlt } from "react-icons/fa";
import { formatTimestampWithRelativeDate } from "../../../utils/date.util";
import { Badge } from "../../users/components/UsersBadge";
import { userRepository } from "../../users/repositories/user.repository";
import { useNavigate } from "react-router-dom";
import { OnboardingReportsTableSkeleton } from "./OnboardingReportsTableSkeleton";
import { useTheme } from "src/context/theme.context";

const getStatusBadgeColor = (status: string, isDark: boolean) => {
  const darkMap: Record<string, string> = {
    INIT: "bg-yellow-900/20 text-yellow-300 border border-yellow-700/50",
    IN_REVIEW: "bg-blue-900/20 text-blue-300 border border-blue-700/50",
    RESOLVED: "bg-green-900/20 text-green-300 border border-green-700/50",
    IGNORED: "bg-gray-900/20 text-gray-300 border border-gray-700/50",
  };

  const lightMap: Record<string, string> = {
    INIT: "bg-yellow-50 text-yellow-700 border border-yellow-200",
    IN_REVIEW: "bg-blue-50 text-blue-700 border border-blue-200",
    RESOLVED: "bg-green-50 text-green-700 border border-green-200",
    IGNORED: "bg-gray-50 text-gray-700 border border-gray-200",
  };

  return isDark ? darkMap[status] || "bg-gray-800 text-gray-300 border border-gray-600"
                : lightMap[status] || "bg-gray-50 text-gray-700 border border-gray-200";
};

const getScreenNameDisplay = (screenName: string) => {
  const displayMap: Record<string, string> = {
    ROLE_SELECTION: "Role Selection",
    LEARNING_LANGUAGE_SELECTION: "Learning Language",
    COACHING_LANGUAGE_SELECTION: "Coaching Language",
    PERSONAL_INFO: "Personal Info",
    COACH_NATIVE_CHECK: "Coach Native Check",
  };
  return displayMap[screenName] || screenName;
};

// Modal Component for viewing report details
const ReportModal: React.FC<{
  report: OnboardingReport | null;
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
}> = ({ report, isOpen, onClose, isDarkMode }) => {
  const [selectedAttachment, setSelectedAttachment] = useState<OnboardingReportAttachment | null>(null);

  if (!isOpen || !report) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div
        className={`relative max-w-4xl w-full max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl ${
          isDarkMode ? "bg-gray-800 border border-gray-700" : "bg-white border border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b ${
            isDarkMode ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50"
          }`}
        >
          <div>
            <h2 className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              Report Details
            </h2>
            <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
              {report.email} • {getScreenNameDisplay(report.screenName)}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode
                ? "hover:bg-gray-700 text-gray-400 hover:text-white"
                : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
            }`}
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="p-6 space-y-6">
            {/* User Info */}
            <div className={`p-4 rounded-lg ${isDarkMode ? "bg-gray-700/50" : "bg-gray-50"}`}>
              <h3 className={`font-semibold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                User Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                    Email:
                  </span>
                  <p className={`text-sm mt-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                    {report.email}
                  </p>
                </div>
                <div>
                  <span className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                    User ID:
                  </span>
                  <p className={`text-sm mt-1 font-mono ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}>
                    {report.userId}
                  </p>
                </div>
                <div>
                  <span className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                    Platform:
                  </span>
                  <p className={`text-sm mt-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                    {report.platform} v{report.appVersion}
                  </p>
                </div>
                <div>
                  <span className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                    Status:
                  </span>
                  <div className="mt-1">
                    <Badge
                      label={report.status}
                      classes={`${getStatusBadgeColor(report.status, isDarkMode)} min-w-[80px] h-[28px]`}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Full Message */}
            <div className={`p-4 rounded-lg ${isDarkMode ? "bg-gray-700/50" : "bg-gray-50"}`}>
              <h3 className={`font-semibold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                Message
              </h3>
              <div className={`p-4 rounded-lg border ${
                isDarkMode ? "bg-gray-800 border-gray-600 text-gray-200" : "bg-white border-gray-200 text-gray-800"
              }`}>
                <p className="whitespace-pre-wrap leading-relaxed">{report.message}</p>
              </div>
            </div>

            {/* Attachments */}
            {report.attachments && report.attachments.length > 0 && (
              <div className={`p-4 rounded-lg ${isDarkMode ? "bg-gray-700/50" : "bg-gray-50"}`}>
                <h3 className={`font-semibold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                  Attachments ({report.attachments.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {report.attachments.map((attachment, index) => (
                    <div
                      key={index}
                      className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                        selectedAttachment === attachment
                          ? isDarkMode ? "border-blue-400 shadow-lg shadow-blue-400/20" : "border-blue-500 shadow-lg shadow-blue-500/20"
                          : isDarkMode ? "border-gray-600 hover:border-gray-500" : "border-gray-300 hover:border-gray-400"
                      }`}
                      onClick={() => setSelectedAttachment(attachment)}
                    >
                      <div className={`aspect-video flex items-center justify-center ${
                        isDarkMode ? "bg-gray-800" : "bg-gray-100"
                      }`}>
                        {attachment.fileType === "IMAGE" ? (
                          <img
                            src={attachment.fileUrl}
                            alt="Attachment thumbnail"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = `
                                  <div class="text-center">
                                    <svg class="w-12 h-12 mx-auto mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}" fill="currentColor" viewBox="0 0 20 20">
                                      <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"></path>
                                    </svg>
                                    <p class="text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}">Image</p>
                                  </div>
                                `;
                              }
                            }}
                          />
                        ) : (
                          <video
                            src={attachment.fileUrl}
                            className="w-full h-full object-cover"
                            muted
                            onError={(e) => {
                              const target = e.target as HTMLVideoElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = `
                                  <div class="text-center">
                                    <svg class="w-12 h-12 mx-auto mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"></path>
                                    </svg>
                                    <p class="text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}">Video</p>
                                  </div>
                                `;
                              }
                            }}
                          />
                        )}
                      </div>
                      <div className={`absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center`}>
                        <FaExpand className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Attachment Preview */}
            {selectedAttachment && (
              <div className={`p-4 rounded-lg border-2 ${
                isDarkMode ? "bg-gray-700/50 border-blue-400" : "bg-blue-50 border-blue-300"
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <h4 className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                    {selectedAttachment.fileType} Preview
                  </h4>
                  <button
                    onClick={() => setSelectedAttachment(null)}
                    className={`p-1 rounded ${
                      isDarkMode ? "hover:bg-gray-600 text-gray-400" : "hover:bg-gray-200 text-gray-600"
                    }`}
                  >
                    <FaTimes className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex justify-center">
                  {selectedAttachment.fileType === "IMAGE" ? (
                    <img
                      src={selectedAttachment.fileUrl}
                      alt="Attachment"
                      className="max-w-full max-h-96 rounded-lg shadow-lg"
                      onError={(e) => {
                        console.log('Image failed to load:', selectedAttachment.fileUrl);
                        const target = e.target as HTMLImageElement;
                        target.src = "https://via.placeholder.com/400x300?text=Image+Not+Found";
                      }}
                      onLoad={() => {
                        console.log('Image loaded successfully:', selectedAttachment.fileUrl);
                      }}
                    />
                  ) : (
                    <video
                      controls
                      className="max-w-full max-h-96 rounded-lg shadow-lg"
                      src={selectedAttachment.fileUrl}
                      onError={(e) => {
                        console.log('Video failed to load:', selectedAttachment.fileUrl);
                      }}
                      onLoad={() => {
                        console.log('Video loaded successfully:', selectedAttachment.fileUrl);
                      }}
                    >
                      Your browser does not support the video tag.
                    </video>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className={`flex justify-end p-6 border-t ${
            isDarkMode ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            onClick={onClose}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              isDarkMode
                ? "bg-gray-700 hover:bg-gray-600 text-white"
                : "bg-gray-200 hover:bg-gray-300 text-gray-800"
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const OnboardingReportRow: React.FC<{
  report: OnboardingReport;
  isDarkMode: boolean;
  onStatusUpdate: (reportId: string, status: string) => void;
  onViewDetails: (report: OnboardingReport) => void;
  existingUsers: Set<string>;
  onUserClick: (userId: string) => void;
}> = ({ report, isDarkMode, onStatusUpdate, onViewDetails, existingUsers, onUserClick }) => {


  return (
    <tr
      className={`transition-all duration-300 hover:transition-colors duration-300 hover:scale-[1.01] hover:shadow-lg group align-middle ${
        isDarkMode
          ? "hover:bg-gradient-to-r hover:from-gray-700/80 hover:to-gray-600/80 bg-gray-800/60"
          : "hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-indigo-50/80 bg-white/60"
      }`}
    >
      <td className="px-2 py-2 min-w-[200px] align-middle">
        <div className="flex items-center space-x-2">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
            isDarkMode ? "bg-gradient-to-r from-blue-500 to-purple-500" : "bg-gradient-to-r from-blue-400 to-indigo-500"
          }`}>
            <span className="text-white font-semibold text-xs">
              {report.email.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="space-y-0.5 min-w-0 flex-1">
            <div className={`text-xs font-semibold truncate ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
              {report.email}
            </div>
            <div className="flex items-center space-x-1">
              <div
                className={`font-mono text-xs tracking-wide px-1.5 py-0.5 rounded transition-all duration-300 flex items-center space-x-1 ${
                  existingUsers.has(report.userId)
                    ? `cursor-pointer hover:opacity-80 ${
                        isDarkMode
                          ? "text-blue-300 bg-blue-900/30 border border-blue-700/50 hover:bg-blue-800/40"
                          : "text-blue-600 bg-blue-50 border border-blue-200/50 hover:bg-blue-100"
                      }`
                    : isDarkMode
                    ? "text-gray-400 bg-gray-800/50 border border-gray-600/50"
                    : "text-gray-500 bg-gray-100 border border-gray-300/50"
                }`}
                onClick={() => existingUsers.has(report.userId) && onUserClick(report.userId)}
                title={existingUsers.has(report.userId) ? "Click to view user profile" : "User profile not found"}
              >
                <span className="truncate">{report.userId}</span>
                {existingUsers.has(report.userId) && (
                  <FaExternalLinkAlt className="w-3 h-3 flex-shrink-0 opacity-60" />
                )}
              </div>
              {existingUsers.has(report.userId) && (
                <FaCheckCircle className={`w-3 h-3 flex-shrink-0 ${isDarkMode ? "text-green-400" : "text-green-500"}`} title="User exists" />
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="px-2 py-2 whitespace-nowrap align-middle">
        <div className="flex items-center">
          <select
            value={report.status}
            onChange={(e) => onStatusUpdate(report.id, e.target.value)}
            className={`px-1.5 py-1 rounded border text-xs font-medium transition-all duration-300 transform hover:scale-105 ${
              report.status === 'INIT'
                ? (isDarkMode ? "bg-yellow-900/20 border-yellow-600 text-yellow-300" : "bg-yellow-50 border-yellow-300 text-yellow-700")
                : report.status === 'IN_REVIEW'
                ? (isDarkMode ? "bg-blue-900/20 border-blue-600 text-blue-300" : "bg-blue-50 border-blue-300 text-blue-700")
                : report.status === 'RESOLVED'
                ? (isDarkMode ? "bg-green-900/20 border-green-600 text-green-300" : "bg-green-50 border-green-300 text-green-700")
                : (isDarkMode ? "bg-gray-700 border-gray-600 text-gray-300" : "bg-gray-50 border-gray-300 text-gray-600")
            }`}
          >
            <option value="INIT">🔄 Init</option>
            <option value="IN_REVIEW">⏱️ In Review</option>
            <option value="RESOLVED">✅ Resolved</option>
            <option value="IGNORED">🚫 Ignored</option>
          </select>
        </div>
      </td>
      <td className="px-2 py-2 whitespace-nowrap align-middle">
        <div
          className={`text-xs px-1.5 py-1 rounded border transition-colors duration-300 ${
            isDarkMode
              ? "text-gray-300 bg-purple-900/20 border-purple-700/50"
              : "text-gray-700 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200/50"
          }`}
        >
          {formatTimestampWithRelativeDate(report.createdAt)}
        </div>
      </td>
      <td className="px-2 py-2 whitespace-nowrap align-middle">
        <button
          onClick={() => onViewDetails(report)}
          className="px-3 py-1.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 hover:from-emerald-600 hover:via-teal-600 hover:to-cyan-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-2 flex items-center space-x-1"
          title="View full report details"
        >
          <span className="text-xs">Details</span>
        </button>
      </td>
    </tr>
  );
};

export const OnboardingReportsList: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const [updateStatus] = useUpdateOnboardingReportStatusMutation();
  const [selectedReport, setSelectedReport] = useState<OnboardingReport | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [existingUsers, setExistingUsers] = useState<Set<string>>(new Set());

  const { data, numberOfPages, currentPage, next, prev, isError, error, isLoading, isFetching } = usePagination<OnboardingReport>({
    useGetDataQuery: useGetOnboardingReportsQuery,
    useCountData: useCountOnboardingReportsQuery,
  });

  const { data: totalCount } = useCountOnboardingReportsQuery({});

  const handleStatusUpdate = async (reportId: string, status: string) => {
    try {
      await updateStatus({ reportId, status });
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const handleViewDetails = (report: OnboardingReport) => {
    setSelectedReport(report);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedReport(null);
  };

  const checkUserExistence = async (userIds: string[]) => {
    // Remove duplicates and limit to prevent too many API calls
    const uniqueUserIds = Array.from(new Set(userIds)).slice(0, 20); // Limit to 20 users max

    try {
      const userPromises = uniqueUserIds.map(async (userId) => {
        try {
          const user = await userRepository.findById(userId);
          return user ? userId : null;
        } catch (error) {
          return null;
        }
      });

      const results = await Promise.all(userPromises);
      const existing = new Set(results.filter((id): id is string => id !== null));
      setExistingUsers(existing);
    } catch (error) {
      console.error('Error checking user existence:', error);
      setExistingUsers(new Set());
    }
  };

  const handleUserClick = (userId: string) => {
    if (existingUsers.has(userId)) {
      // Navigate to user details page
      navigate(`/users/${userId}`);
    }
  };

  // Check user existence when data loads
  useEffect(() => {
    if (data && data.length > 0) {
      const userIds = data.map(report => report.userId);
      checkUserExistence(userIds);
    }
  }, [data]);

  if (isError) {
    const rawErrorText = (() => {
      if (!error) return "Unknown error";
      const anyErr: any = error;
      if (typeof anyErr === "string") return anyErr;
      if (anyErr?.data) return String(anyErr.data);
      if (anyErr?.message) return String(anyErr.message);
      try { return JSON.stringify(anyErr); } catch { return String(anyErr); }
    })();

    return (
      <div
        className={`text-red-500 p-6 border border-red-200 rounded-2xl max-w-4xl mx-auto shadow-lg ${
          isDarkMode ? "bg-red-900/20" : "bg-gradient-to-r from-red-50 to-pink-50"
        }`}
      >
        <h3 className="font-semibold text-lg mb-3">Error Loading Onboarding Reports</h3>
        <div className={`mb-4 text-sm ${isDarkMode ? "text-red-200" : "text-red-700"}`}>
          <pre className="whitespace-pre-wrap break-words">{rawErrorText}</pre>
        </div>
        <button onClick={() => window.location.reload()} className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600" type="button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      className={`container mx-auto px-4 antialiased min-h-screen transition-colors duration-300 ${
        isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"
      }`}
    >
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <div className={`p-3 rounded-2xl ${
            isDarkMode ? "bg-blue-900/20 border border-blue-700/30" : "bg-blue-50 border border-blue-200"
          }`}>
            <FaFileAlt className={`w-8 h-8 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`} />
          </div>
          <div>
            <h1
              className={`text-4xl font-extrabold tracking-tight ${
                isDarkMode
                  ? "bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent"
                  : "bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent"
              }`}
            >
              Onboarding Reports
            </h1>
            <p className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
              Monitor and manage user onboarding feedback
            </p>
          </div>
        </div>
      </div>

      <div
        className={`shadow-2xl rounded-2xl overflow-hidden border backdrop-blur-sm transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-800 border-gray-700"
            : "bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 border-blue-200/50"
        }`}
      >
        <div
          className={`px-8 py-6 border-b transition-colors duration-300 ${
            isDarkMode
              ? "border-gray-700 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800"
              : "bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600"
          }`}
        >
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center space-x-4 mb-4">
                <div className={`p-3 rounded-xl ${
                  isDarkMode ? "bg-gray-700/50" : "bg-white/20"
                } backdrop-blur-sm`}>
                  <FaFileAlt className={`w-6 h-6 ${isDarkMode ? "text-blue-400" : "text-white"}`} />
                </div>
                <div>
                  <span className={`text-xl font-bold ${isDarkMode ? "text-gray-100" : "text-white"}`}>
                    Reports Overview
                  </span>
                  <div className={`text-sm ${isDarkMode ? "text-gray-300" : "text-emerald-100"}`}>
                    {totalCount || 0} total reports • Page {currentPage} of {numberOfPages}
                  </div>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className={`p-4 rounded-xl ${
                  isDarkMode ? "bg-gray-700/30 border border-gray-600" : "bg-white/10 border border-white/20"
                } backdrop-blur-sm`}>
                  <div className={`text-2xl font-bold ${isDarkMode ? "text-blue-400" : "text-white"}`}>
                    {totalCount || 0}
                  </div>
                  <div className={`text-sm ${isDarkMode ? "text-gray-300" : "text-emerald-100"}`}>
                    Total Reports
                  </div>
                </div>
                <div className={`p-4 rounded-xl ${
                  isDarkMode ? "bg-gray-700/30 border border-gray-600" : "bg-white/10 border border-white/20"
                } backdrop-blur-sm`}>
                  <div className={`text-2xl font-bold ${isDarkMode ? "text-yellow-400" : "text-yellow-300"}`}>
                    {data?.filter(r => r.status === 'INIT').length || 0}
                  </div>
                  <div className={`text-sm ${isDarkMode ? "text-gray-300" : "text-emerald-100"}`}>
                    Pending
                  </div>
                </div>
                <div className={`p-4 rounded-xl ${
                  isDarkMode ? "bg-gray-700/30 border border-gray-600" : "bg-white/10 border border-white/20"
                } backdrop-blur-sm`}>
                  <div className={`text-2xl font-bold ${isDarkMode ? "text-green-400" : "text-green-300"}`}>
                    {data?.filter(r => r.status === 'RESOLVED').length || 0}
                  </div>
                  <div className={`text-sm ${isDarkMode ? "text-gray-300" : "text-emerald-100"}`}>
                    Resolved
                  </div>
                </div>
                <div className={`p-4 rounded-xl ${
                  isDarkMode ? "bg-gray-700/30 border border-gray-600" : "bg-white/10 border border-white/20"
                } backdrop-blur-sm`}>
                  <div className={`text-2xl font-bold ${isDarkMode ? "text-purple-400" : "text-purple-300"}`}>
                    {data?.filter(r => r.attachments && r.attachments.length > 0).length || 0}
                  </div>
                  <div className={`text-sm ${isDarkMode ? "text-gray-300" : "text-emerald-100"}`}>
                    With Attachments
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={toggleTheme}
                className={`p-3 rounded-xl transition-all duration-300 transform hover:scale-105 ${
                  isDarkMode
                    ? "bg-gray-700/50 text-yellow-400 hover:bg-gray-600 border border-gray-600"
                    : "bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm border border-white/30"
                }`}
                title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {isDarkMode ? <FaSun className="w-5 h-5" /> : <FaMoon className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full divide-y transition-colors duration-300">
            <thead
              className={`transition-colors duration-300 ${
                isDarkMode
                  ? "bg-gray-700/80 divide-gray-600"
                  : "bg-gradient-to-r from-blue-50/80 to-indigo-50/80 divide-blue-200/30"
              }`}
            >
              <tr>
                {["User", "Status", "Created", "Details"].map((header) => (
                  <th
                    key={header}
                    className={`px-2 py-2 text-center text-xs font-semibold uppercase tracking-wider border-b transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200 border-gray-600" : "text-blue-700 border-blue-200/50"
                    }`}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody
              className={`divide-y transition-colors duration-300 ${
                isDarkMode ? "bg-gray-800/80 divide-gray-700" : "bg-white/80 divide-blue-100/50"
              }`}
            >
              {isLoading || isFetching ? (
                <OnboardingReportsTableSkeleton rows={10} isDarkMode={isDarkMode} />
              ) : data && data.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-2 py-12 text-center">
                    <div className="text-center space-y-6">
                      <div className="relative">
                        <div
                          className={`w-24 h-24 rounded-2xl flex items-center justify-center mx-auto ${
                            isDarkMode
                              ? "bg-gradient-to-br from-blue-900/20 to-purple-900/20 border border-blue-700/30"
                              : "bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200/50"
                          }`}
                        >
                          <FaFileAlt className={`w-12 h-12 ${isDarkMode ? "text-blue-400" : "text-blue-500"}`} />
                        </div>
                        <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">✓</span>
                        </div>
                      </div>
                      <div>
                        <div className={`text-2xl font-bold mb-3 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}>
                          All Caught Up!
                        </div>
                        <div className={`text-lg ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                          No pending reports to review
                        </div>
                        <div className={`text-sm mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                          New onboarding reports will appear here when users submit feedback
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                data &&
                data.map((report) => (
                  <OnboardingReportRow
                    key={report.id}
                    report={report}
                    isDarkMode={isDarkMode}
                    onStatusUpdate={handleStatusUpdate}
                    onViewDetails={handleViewDetails}
                    existingUsers={existingUsers}
                    onUserClick={handleUserClick}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        <div
          className={`px-8 py-6 border-t transition-colors duration-300 ${
            isDarkMode
              ? "border-gray-700 bg-gray-700/60"
              : "border-blue-200/50 bg-gradient-to-r from-blue-50/60 to-indigo-50/60"
          }`}
        >
          <Pagination currentPage={currentPage} numberOfPages={numberOfPages || 1} prev={prev} next={next} isLoading={isLoading || isFetching} />
        </div>
      </div>

      {/* Modal */}
      <ReportModal
        report={selectedReport}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};
